import type { GASetupDatalayer } from '@integration-layer/schemas/GAEvents/dataLayerSetup'
import { notLoadCookieBanner } from '@integration-layer/utils/oneTrustLocales'

declare global {
  interface Window {
    _uxa: Array<unknown[]>
    OnetrustActiveGroups: string | undefined
  }
  // Variable added by the ContentSquare integration
  let CS_CONF: unknown
}

/**
 * Nuxt plugin for Contentsquare analytics integration.
 *
 * Replaces the equivalent GTM tag with a first-party integration:
 * - script is loaded on the first Armani_DL_Setup GA event if tracking consent is present
 * - if consent comes later than the GA event, the integration picks up the last event's payload and fires that
 * - if consent is revoked, the integration pushes a 'optout' event to Contentsquare
 * - on consent-exempt locales (no OneTrust, e.g. ja-jp) the script runs without consent
 */
export default defineNuxtPlugin(nuxtApp => {
  /** Contentsquare project tag id (static; identifies the Armani CS project). */
  const CONTENTSQUARE_TAG_ID = 'dde9c57ce1d33'
  const ONETRUST_CATEGORY = 'C0002'
  //
  const CONSENT_EXEMPT_CONTENTSQUARE_LOCALES = ['ja-jp']

  let trackingEnabled = false
  let lastDlSetupPayload: null | GASetupDatalayer = null
  let scriptLoaded = false

  const contentSquareScript = useScript(
    {
      key: 'contentsquare',
      src: `https://t.contentsquare.net/uxa/${CONTENTSQUARE_TAG_ID}.js`,
      crossorigin: 'anonymous',
    },
    {
      trigger: 'manual',
    }
  )

  if (import.meta.server) return

  const { countryLocale } = useCountryLocale()
  // Fail-safe: the bypass is valid only for the OneTrust-exempt countries who also have Contentsquare
  const isConsentExemptLocale = () =>
    notLoadCookieBanner.includes(countryLocale.value) &&
    CONSENT_EXEMPT_CONTENTSQUARE_LOCALES.includes(countryLocale.value)

  // startup consent check in case OneTrust script has executed before ContentSquare plugin
  evaluateConsent()

  window.addEventListener('OneTrustGroupsUpdated', () => {
    evaluateConsent()
    if (trackingEnabled && !!lastDlSetupPayload) {
      setVariablesAndTriggerPush(lastDlSetupPayload)
    }
  })

  // Ensure the _uxa queue exists before any pushes — the vendor script drains and replaces this array once it loads.
  window._uxa = window._uxa || []

  let piiSelectorsPushed = false

  nuxtApp.hook('armani_dl_setup-event-fired', (payload: GASetupDatalayer) => {
    lastDlSetupPayload = payload
    if (!trackingEnabled && !isConsentExemptLocale()) return
    setVariablesAndTriggerPush(payload)
  })

  // HELPER FUNCTIONS
  function evaluateConsent() {
    // Exempt locales never flip tracking or push optout
    if (isConsentExemptLocale()) return
    const groups = window?.OnetrustActiveGroups
    const hasUserConsent = !!groups?.includes(ONETRUST_CATEGORY)
    const isAlreadyTracking = !!trackingEnabled

    // new consent accepted
    if (hasUserConsent && !isAlreadyTracking) {
      trackingEnabled = true
      // previous consent revoked
    } else if (!hasUserConsent && isAlreadyTracking) {
      trackingEnabled = false
      window._uxa = window._uxa || []
      window._uxa.push(['optout'])
    }
  }

  /**
   * This function pushes Contentsquare variables,
   * sends the pageview push and loads the script if not previously loaded.
   * Manual loading of the script is necessary because the CS integration
   * fires an automatic pageview push on load.
   */
  function setVariablesAndTriggerPush(payload: GASetupDatalayer) {
    try {
      pushSetQuery()

      pushPIISelectorsOnce()

      pushCustomVariables(payload)

      setScriptOrTrackPageView()
      lastDlSetupPayload = null
    } catch (e) {
      // The original snippet swallows errors silently to avoid breaking
      // page rendering when a payload field is missing. Preserve that.
      if (import.meta.dev) {
        console.error('[Contentsquare] handler error:', e)
      }
    }
  }

  /** Pushes setQuery to _uxa if anonymisation changed the query string. */
  function pushSetQuery() {
    const anonymized = anonymizeQueryString(window.location.search)
    if (window.location.search === anonymized) return
    window._uxa.push(['setQuery', anonymized])
  }

  /** Pushes the PII selectors list to Contentsquare.
   *  Avoids logic duplication on every hook call by relying on `piiSelectorsPushed`.
   *
   * Note: PII selectors are preserved verbatim (including section comments) from the original GTM tag. Do not reorder or dedupe without client sign-off.
   */
  function pushPIISelectorsOnce() {
    const PII_SELECTORS = [
      // --- Informazioni Personali & Messaggi ---
      'span.name',
      'span.welcomeMessage',
      'p.giftMessage',
      'div.userInfo',
      'ul.userInfo',
      'div.ProfileViewModel_BirthDate_Wrapper',
      'div.selectInput_wrapper.day',
      'div.selectInput_wrapper.month',
      'div.selectInput_wrapper.year',

      // --- Indirizzi & Spedizione ---
      'div.addressList',
      'ul.address-list',
      'div.address-list',
      'div.addressData',
      'ul.addressData',
      'div.address-selection',
      'div.default-address',
      'div.shippingAddressRecap',
      'div.checkout-address-recap__wrapper',
      'div.clientAddressWrapper',
      'div.addressHint.fieldRow.autocompleteAddress.StreetWithNumber.validRow',
      'div.pac-container.pac-logo',

      // --- Dati Account & Pagamento ---
      'div.account-overview--box__data',
      'div.creditCards',
      'ul.creditCards',
      'ul.cardTypeList',

      // --- Campi Form & UI ---
      'fieldset.fieldRow.radioGroup.Title',
      'div.selectInput_wrapper',
      'div.countryField',
      'div.AddressFormDataModel_Country_Wrapper',
      'div.selectize-control.single',
      'div.selectize-input.items',
      'span.dialCode',
      'div.inputContainer',
      'div.opc-step-recap__content',
      'div.text-light-6',
      'ul.formkit-listbox',
      'ul.suggestions-list',
      'span.text-medium-4',
      'div.text-medium-6',
      'ul#googleAutocompleteSuggestions.suggestions-list',
      'div.formkit-dropdown-wrapper',
      'div.text-book-6.m-auto.mb-36.flex.flex-col.gap-y-10',
      'div.gap-y-md.flex.flex-col',
      'div.my-account-order-recap.bg-primitives-white.text-book-6.space-y-4.p-6.md:space-y-6 print:px-0',
      'div.my-account-order-recap',
    ]

    if (piiSelectorsPushed) return
    window._uxa.push(['setPIISelectors', { PIISelectors: PII_SELECTORS }])
    piiSelectorsPushed = true
  }

  /**
   * Anonymise the URL query string before sending to Contentsquare.
   *
   * Ported from the client's GTM "Anonymized Querystring" variable: gates
   * the email pattern replace on textSearch looking like an email, then
   * unconditionally replaces firstName / lastName / email param values
   * with CS_ANONYMIZED_* tokens.
   */
  function anonymizeQueryString(rawQuery: string): string {
    let query = rawQuery
    const params = new URLSearchParams(rawQuery)

    const textSearch = params.get('textSearch')
    const firstName = params.get('firstName')
    const lastName = params.get('lastName')
    const email = params.get('email')

    const isEmailLike = /^[\w-_\.]+%40([\w-_]+\.)+[\w_-]{2,4}$/
    const emailInQuery = /[\w-_\.]+%40([\w-_]+\.)+[\w_-]{2,4}/

    if (textSearch && isEmailLike.test(textSearch)) {
      query = query.replace(emailInQuery, 'CS_ANONYMIZED_EMAIL')
    }
    if (firstName) {
      query = query.replace(firstName, 'CS_ANONYMIZED_FIRST_NAME')
    }
    if (lastName) {
      query = query.replace(lastName, 'CS_ANONYMIZED_LAST_NAME')
    }
    if (email) {
      query = query.replace(email, 'CS_ANONYMIZED_EMAIL')
    }

    return query
  }

  /**
   * Pushes custom variables to ContentSquare.
   * The list is adapted from client specs (listed below). Do not edit without client signoff
   *
   * @description
   * 1. **userLogged**         <- 02-DL-01-GLOBAL-02-PARAMUSER-01-USERLOGGED
   * 2. **brand**              <- 02-DL-01-GLOBAL-03-PARAMPAGE-05-BRAND
   * 3. **pageType**           <- 02-DL-01-GLOBAL-03-PARAMPAGE-02-PAGE_TYPE
   * 4. **currentShipping**    <- 02-DL-01-GLOBAL-01-PARAMGLOBAL-04-CURRENT_SHIPPING
   * 5. **department**         <- 02-DL-01-GLOBAL-03-PARAMPAGE-04-DEPARTMENT
   * 6. **userID**             <- 02-DL-01-GLOBAL-02-PARAMUSER-02-USERID
   * 7. **userType**           <- 02-DL-01-GLOBAL-02-PARAMUSER-04-USERTYPE
   * 8. **subPageType**        <- 02-DL-01-GLOBAL-03-PARAMPAGE-03-SUB_PAGE_TYPE
   * 9. **cartID**             <- 02-DL-01-GLOBAL-05-PARAMCART-01-CARTID
   * 10. **checkoutType**       <- 02-DL-01-GLOBAL-07-PARAMSALES-01-CHECKOUT_TYPE
   * 11. **shopMacroCategory**  <- 02-DL-01-GLOBAL-04-PARAMITEMS-04-ITEM_CATEGORY_LEVEL_1
   * 12. **shopMicroCategory**  <- 02-DL-01-GLOBAL-04-PARAMITEMS-05-ITEM_CATEGORY_LEVEL_2
   * 13. **gender**             <- 02-DL-01-GLOBAL-03-PARAMPAGE-06-GENDER
   * 14. **userGender**         <- 02-DL-01-GLOBAL-02-PARAMUSER-06-USERGENDER
   * All use scope 3 (Visit).
   *
   */
  function pushCustomVariables(dlSetupPayload: GASetupDatalayer) {
    const customVars: Array<[number, string, unknown]> = [
      [1, 'userLogged', dlSetupPayload.paramUser.userLogged],
      [2, 'brand', dlSetupPayload.paramPage.brand],
      [3, 'pageType', dlSetupPayload.paramPage.page_type],
      [4, 'currentShipping', dlSetupPayload.paramGlobal.current_shipping],
      [5, 'department', dlSetupPayload.paramPage.department],
      [6, 'userID', dlSetupPayload.paramUser.userID],
      [7, 'userType', dlSetupPayload.paramUser.userType],
      [8, 'subPageType', dlSetupPayload.paramPage.sub_page_type],
      [9, 'cartID', dlSetupPayload.paramCart.cartID],
      [10, 'checkoutType', dlSetupPayload.paramSales.checkout_type],
      [
        11,
        'shopMacroCategory',
        dlSetupPayload.paramItems.item_category_level_1,
      ],
      [
        12,
        'shopMicroCategory',
        dlSetupPayload.paramItems.item_category_level_2,
      ],
      [13, 'gender', dlSetupPayload.paramPage.gender],
      [14, 'userGender', dlSetupPayload.paramUser.userGender],
    ]
    for (const [index, name, value] of customVars) {
      window._uxa.push(['setCustomVariable', index, name, value, 3])
    }
  }

  /**
   * Original GTM control flow — preserved literally. If the vendor
   * SDK has not yet defined the CS_CONF global, push setPath and
   * load the script; otherwise push trackPageview.
   */
  function setScriptOrTrackPageView() {
    const path =
      window.location.pathname + window.location.hash.replace('#', '?__')
    if (!scriptLoaded && typeof CS_CONF === 'undefined') {
      window._uxa.push(['setPath', path])
      contentSquareScript.load()
      scriptLoaded = true
    } else {
      window._uxa.push(['trackPageview', path])
    }
  }
})
